import React, { useMemo } from 'react';
// eslint-disable-next-line jira/import/no-restricted-import
import { SmartCardProvider } from '@atlaskit/link-provider';
import type { ProductType } from '@atlaskit/linking-common';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useGlobalObjectPreviewPanels } from '@atlassian/jira-preview-panels/src/PreviewPanelsProvider.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { ProjectContext } from '@atlassian/jira-router-resources-project-context/src/services/project-context/types.tsx';
import { getCorrectContentURL } from '@atlassian/confluence-page-preview-panel';
import { getIsPreviewPanelEnabled } from '@atlassian/jira-preview-panels-rollout/src/index.tsx';
import { JiraSnippetRenderer } from './JiraSnippetRenderer.tsx';

export const LinkingProviders = ({ children }: { children: React.ReactNode }) => {
	const { data } = useProjectContext();
	const cloudId = useCloudId();

	const product = getProductForSmartCardProvider(data);

	const atlassianIntelligenceProps = useMemo(() => {
		if (!fg('jira-cc-ai-snippet-gate') || !data?.isAIEnabled) {
			return {};
		}

		return {
			renderers: {
				snippet: JiraSnippetRenderer,
			},
		};
	}, [data?.isAIEnabled]);

	if (getIsPreviewPanelEnabled() && fg('jira_preview_panel_m_1_rollout')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const smartCardProviderProps = useGlobalObjectPreviewPanels();
		if (fg('jira-preview-panel-confluence-integration')) {
			const open = smartCardProviderProps.openPreviewPanel;

			// Create a wrapper function that updates the preview panel URL
			// This is used for custom domains to update to the correct content URL and avoid CSP issues
			// eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			const openPreviewPanelWithModifiedURL = useMemo(
				() => (props: Parameters<(typeof smartCardProviderProps)['openPreviewPanel']>[0]) =>
					open({
						...props,
						url: getCorrectContentURL(cloudId, props.url, props.ari),
						ari: props.ari,
					}),
				[open, cloudId],
			);

			return (
				<SmartCardProvider
					{...smartCardProviderProps}
					openPreviewPanel={openPreviewPanelWithModifiedURL}
					product={product}
					isAdminHubAIEnabled={data?.isAIEnabled ?? false}
					{...atlassianIntelligenceProps}
				>
					{children}
				</SmartCardProvider>
			);
		}
		return (
			<SmartCardProvider
				{...smartCardProviderProps}
				product={product}
				isAdminHubAIEnabled={data?.isAIEnabled ?? false}
				{...atlassianIntelligenceProps}
			>
				{children}
			</SmartCardProvider>
		);
	}

	return (
		<SmartCardProvider
			product={product}
			isAdminHubAIEnabled={data?.isAIEnabled ?? false}
			{...atlassianIntelligenceProps}
		>
			{children}
		</SmartCardProvider>
	);
};

const getProductForSmartCardProvider = (
	project: ProjectContext | null | undefined,
): ProductType | undefined => {
	switch (project?.projectType) {
		case 'software':
			return 'JSW';
		case 'business':
			return 'JWM';
		case 'service_desk':
			return 'JSM';
		case 'product_discovery':
			return 'JPD';
		default:
			return undefined;
	}
};
